import { Button, Group, GroupProps, Title, TitleOrder } from '@mantine/core';
import { modals } from '@mantine/modals';

import { CustomerPayments, Declarant, DeclarantPayments } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { EditAdditionalFiscalReference, Table, TableActionEnum, TableActions, TableColumnProps } from '@components';
import { AdditionalFiscalReferenceColumns } from '@enums';
import { IAdditionalFiscalReferenceItem } from '@interfaces';

interface AdditionalFiscalReferenceTableProps {
  handleUpdate(updatedData: CustomerPayments | DeclarantPayments | Partial<Declarant>): void;
  isCustomerFlow: boolean;
  payments: CustomerPayments | DeclarantPayments | undefined;
  titleOrder?: TitleOrder;
  groupProps?: Omit<GroupProps, 'children'>;
}

export const AdditionalFiscalReferenceTable: React.FC<AdditionalFiscalReferenceTableProps> = ({
  handleUpdate,
  isCustomerFlow,
  payments,
  titleOrder,
  groupProps = { justify: 'space-between', align: 'center', mb: 'xl' },
}) => {
  const handleAdditionalFiscalReferenceDelete = () => {
    const initialUpdatedData = {
      ...payments,
      additionalFiscalReference: [],
    };

    const updatedData = (isCustomerFlow ? initialUpdatedData : { payments: initialUpdatedData }) as CustomerPayments | Partial<Declarant>;

    handleUpdate(updatedData);
  };

  const openDeleteAdditionalFiscalReference = () => {
    modals.openConfirmModal({
      title: 'Confirm additional fiscal reference delete',
      children: <div>Are you sure you want to delete the additional fiscal reference? Action can't be undone!</div>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => handleAdditionalFiscalReferenceDelete(),
    });
  };

  const openModal = (reference?: IAdditionalFiscalReferenceItem) => {
    modals.open({
      title: reference ? 'Edit additional fiscal reference' : 'Add additional fiscal reference',
      children: <EditAdditionalFiscalReference reference={reference} onHide={modals.closeAll} onSave={handleSavingAdditionalFiscalReference} />,
    });
  };

  const handleTableAction = (actionType: string, row: IAdditionalFiscalReferenceItem) => {
    if (actionType === 'edit') {
      openModal(row);
    }

    if (actionType === 'delete') {
      openDeleteAdditionalFiscalReference();
    }
  };

  const columns: TableColumnProps<any>[] = [
    {
      name: AdditionalFiscalReferenceColumns.ROLE,
      cell: (row) => row.role,
    },
    {
      name: AdditionalFiscalReferenceColumns.VAT_IDENTIFICATION_NUMBER,
      cell: (row) => row.vatIdentificationNumber,
    },
    {
      name: AdditionalFiscalReferenceColumns.ACTIONS,
      sticky: 'right',
      cell: (row: IAdditionalFiscalReferenceItem) => (
        <TableActions
          actions={[
            {
              icon: IconFactory.view,
              text: 'View details',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            },
          ]}
          onClick={(actionType) => {
            handleTableAction(actionType, row);
          }}
        />
      ),
    },
  ];

  const handleSavingAdditionalFiscalReference = (updatedAdditionalFiscalReference: IAdditionalFiscalReferenceItem) => {
    const newAdditionalFiscalReference = {
      role: updatedAdditionalFiscalReference.role,
      vatIdentificationNumber: updatedAdditionalFiscalReference.vatIdentificationNumber,
    };

    const initialUpdatedData = {
      ...payments,
      additionalFiscalReference: [newAdditionalFiscalReference],
    };

    const updatedData = (isCustomerFlow ? initialUpdatedData : { payments: initialUpdatedData }) as CustomerPayments | Partial<Declarant>;

    handleUpdate(updatedData);
  };

  const isAddNewAdditionalFiscalReferenceDisable = payments?.additionalFiscalReference?.length >= 1;

  return (
    <>
      <Group {...groupProps}>
        <Title order={titleOrder} fw={600}>
          Additional fiscal reference
        </Title>
        <Button disabled={isAddNewAdditionalFiscalReferenceDisable} onClick={() => openModal()} size="lg" variant="outline">
          Add new
        </Button>
      </Group>

      <Table columns={columns} dataSource={payments?.additionalFiscalReference || []} pagination verticalSpacing="xs" />
    </>
  );
};
